import React, { useEffect } from "react";
import { Redirect, Route } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";

import { bootstrapSession, fetchMe } from "../store/auth/profile/actions";
import Forbidden from "../Components/Common/Forbidden";

/**
 * Wraps the authenticated section of the app.
 *
 * State-machine over `state.Profile.status`:
 *   idle/loading  → spinner
 *   error         → "couldn't reach the server" + Retry (session NOT ended)
 *   anonymous     → <Redirect to="/login">
 *   authenticated → render children
 *
 * On first mount, dispatch `bootstrapSession` so a freshly-loaded tab hydrates
 * Profile state by calling /auth/me (the session cookie is httpOnly, so the
 * server is the only authority on whether a session exists).
 *
 * This is the ONLY place bootstrapSession is dispatched. It used to also fire
 * from App.js, which doubled every page load's /auth/me calls against the
 * backend's 100-req/15-min per-IP limiter — and a 429 used to read as a logout.
 *
 * Only `anonymous` may redirect to /login. A server we cannot reach is `error`:
 * the cookie is probably still valid, so signing the user out would be a lie.
 */
const AuthProtected = (props) => {
  const dispatch = useDispatch();
  const status = useSelector((state) => state.Profile.status);

  useEffect(() => {
    if (status === "idle") {
      dispatch(bootstrapSession());
    }
  }, [status, dispatch]);

  if (status === "idle" || status === "loading") {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-2 border-gray-300 border-t-gray-900"></div>
      </div>
    );
  }

  if (status === "error") {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="bl-glass" style={{ padding: "1.5rem 1.75rem", textAlign: "center", maxWidth: 420 }}>
          <p style={{ fontWeight: 600, marginBottom: 6, color: "var(--bl-text-strong)" }}>
            Couldn’t reach the server
          </p>
          <p style={{ fontSize: 13, color: "var(--bl-text-soft)", marginBottom: 16 }}>
            You’re still signed in — this is a connection problem, not a sign-out.
          </p>
          <button className="bl-btn-primary" onClick={() => dispatch(fetchMe())} style={{ padding: "0.5rem 1.25rem" }}>
            Retry
          </button>
        </div>
      </div>
    );
  }

  if (status === "anonymous") {
    return <Redirect to={{ pathname: "/login", state: { from: props.location } }} />;
  }

  return <>{props.children}</>;
};

/**
 * Per-route component wrapper. If `requiredPermission` is set, gates rendering on
 * the current user having that permission (super-admins bypass). On mismatch,
 * renders `<Forbidden />` in place — DO NOT redirect, the user is authenticated.
 */
const AccessRoute = ({ component: Component, requiredPermission, ...rest }) => {
  const is_super_admin = useSelector((state) => state.Profile.is_super_admin);
  const permissions = useSelector((state) => state.Profile.permissions);

  return (
    <Route
      {...rest}
      render={(props) => {
        if (requiredPermission && !is_super_admin && !permissions.includes(requiredPermission)) {
          return <Forbidden missing={requiredPermission} />;
        }
        return <Component {...props} />;
      }}
    />
  );
};

export { AuthProtected, AccessRoute };
